import { StyleSheet, View, ViewProps } from 'react-native'
import { Ref, useImperativeHandle, useRef, useState } from 'react'
import { Draft, useBuilding, useCompany, useFloor, useWorkspaces, Workspace } from 'zens-store'
import { useTranslation } from 'react-i18next'
import { Color, localizedDate, SliderInput, Spacing, Text } from 'zens-storybook'
import { observer } from 'mobx-react'
import { jsPDF } from 'jspdf'
import { Canvg } from 'canvg'

import { Form } from '../form'

import { LabelType } from '../floor/floorplan'

import Orientation from './orientation'
import FloorplanView from './view'

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flex: 1,
  },
  slider: {
    flex: 1,
    marginTop: Spacing.XS,
  },
  label: {
    marginTop: Spacing.S,
    marginLeft: -20,
    width: 40,
    textAlign: 'center',
  },
  hidden: {
    display: 'none',
  },
})

export interface Data {
  id: number
  orientation: 'portrait' | 'landscape'
  scale: number
}

// @ts-expect-error Load canvg to window
window.canvg = Canvg

interface Props extends ViewProps {
  companyId: number
  floorId: number
  labels?: LabelType
  onCancel?: () => void
  onDownload?: () => void
  test_ref?: Ref<any>
}

const MARGIN = 200

const FloorplanDownload = (props: Props) => {
  const { companyId, floorId, labels, onCancel, onDownload, test_ref, style, ...otherProps } = props
  const [contentSize, setContentSize] = useState({ width: 0, height: 0 })
  const company = useCompany({ companyId })
  const floor = useFloor({ companyId, floorId })
  const building = useBuilding({ companyId, buildingId: floor.data?.buildingId || 0 })
  const workspaces = useWorkspaces({ companyId, filter: { floorId: [floorId] } })
  const draft = useRef(new Draft<Data>({ id: 1, orientation: 'landscape', scale: 1 })).current
  const { t } = useTranslation()
  const scale_ref = useRef<typeof SliderInput>(null)

  useImperativeHandle(test_ref, () => ({
    changeScale: (value: number[]) => {
      // @ts-expect-error Change value is undocumented test only function
      scale_ref.current?.changeValue([value])
    },
  }))

  const documentSize =
    draft.data.orientation === 'landscape' ? { width: 2970, height: 2100 } : { width: 2100, height: 2970 }
  const availableSize = {
    width: draft.data.scale * documentSize.width - 2 * MARGIN,
    height: draft.data.scale * documentSize.height - 2 * MARGIN,
  }
  const scaledWidth = availableSize.height * (contentSize.width / contentSize.height)
  const scaledHeight = availableSize.width * (contentSize.height / contentSize.width)
  const floorplanSize =
    scaledWidth > availableSize.width
      ? { width: availableSize.width, height: scaledHeight }
      : { width: scaledWidth, height: availableSize.height }

  const workspaceItems = workspaces.data?.data

  function borderColorForWorkspace() {
    return labels ? Color.modifiedGrey : Color.premiumBlack
  }

  function labelForWorkspace(workspace: Workspace) {
    return labels === 'name'
      ? workspace?.name
      : labels === 'serialnumber'
        ? workspace?.product?.serialNumber
        : undefined
  }

  async function download() {
    const element = document.getElementById('FloorplanDownload')

    const title = `${company.data?.name} - ${building.data?.name} - ${floor.data?.name}`
    const svg = `${element?.outerHTML}`
    const doc = new jsPDF({ orientation: draft.data.orientation, unit: 'px', format: [2100, 2970] })
    doc.setFontSize(48)
    for (let col = 0; col < draft.data.scale; col++) {
      for (let row = 0; row < draft.data.scale; row++) {
        await doc.addSvgAsImage(
          svg,
          (draft.data.scale * documentSize.width - floorplanSize.width) / 2 - col * documentSize.width,
          (draft.data.scale * documentSize.height - floorplanSize.height) / 2 - row * documentSize.height,
          floorplanSize.width,
          floorplanSize.height,
        )
        const titleWidth = doc.getTextWidth(title)
        doc.setFillColor('#fff')
        doc.setDrawColor('#fff')
        doc.rect((documentSize.width - titleWidth) / 2 - 5, MARGIN - 75 - 48 + 5, titleWidth + 10, 48 + 10, 'F')
        doc.fill()
        doc.text(title, (documentSize.width - titleWidth) / 2, MARGIN - 75)
        const page = `Page ${'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(col)}${row + 1} (${row + col * draft.data.scale + 1}/${
          draft.data.scale * draft.data.scale
        })`
        const pageWidth = doc.getTextWidth(page)
        doc.setFillColor('#fff')
        doc.setDrawColor('#fff')
        doc.rect(
          documentSize.width - pageWidth - MARGIN - 5,
          documentSize.height - 75 - 48 + 5,
          pageWidth + 10,
          48 + 10,
          'F',
        )
        doc.fill()
        doc.text(page, documentSize.width - pageWidth - MARGIN, documentSize.height - 75)

        const date = `${localizedDate(new Date())}`
        const dateWidth = doc.getTextWidth(date)
        doc.setFillColor('#fff')
        doc.setDrawColor('#fff')
        doc.rect(MARGIN - 5, documentSize.height - 75 - 48 + 5, dateWidth, 48 + 10, 'F')
        doc.fill()
        doc.text(date, MARGIN, documentSize.height - 75)

        if (col < draft.data.scale - 1 || row < draft.data.scale - 1) {
          doc.addPage()
        }
      }
    }
    doc.fill()
    doc.save('map.pdf')
    onDownload?.()
  }

  const config = {
    fields: [
      {
        key: 'orientation',
        label: 'Orientation',
        FieldInputComponent: Orientation,
      },
      {
        key: 'scale',
        label: 'Scale',
        FieldInputComponent: SliderInput,
        fieldProps: {
          style: styles.slider,
          minimumValue: 1,
          maximumValue: 10,
          step: 1,
          renderBelowThumbComponent: () => <Text style={styles.label}>{draft.data.scale}x</Text>,
          test_ref: scale_ref,
        },
      },
    ],
  }

  const buttons = [
    {
      title: t('Download'),
      onPress: download,
      validate: true,
      contentContainerStyle: {
        minWidth: 120,
      },
      testID: 'FloorplanDownload.Download',
    },
    {
      title: t('Cancel'),
      onPress: async () => onCancel?.(),
      light: true,
      contentContainerStyle: {
        minWidth: 120,
      },
      testID: 'FloorplanDownload.Cancel',
    },
  ]

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.hidden}>
        <FloorplanView
          companyId={companyId}
          floorId={floorId}
          data={workspaceItems}
          width={floorplanSize.width}
          height={floorplanSize.height}
          id="FloorplanDownload"
          onContentSizeChange={setContentSize}
          itemBorderColor={borderColorForWorkspace}
          itemLabel={(item) => labelForWorkspace(item as Workspace)}
          labelFontSize={48 / draft.data.scale}
        />
      </View>
      <Form draft={draft} config={config} buttons={buttons} />
    </View>
  )
}

export default observer(FloorplanDownload)
